// @flow
import * as React from 'react';
import {
    Box, Text, TextInput,
} from 'grommet';
import {
    defineMessages, FormattedMessage, injectIntl, IntlShape,
} from 'react-intl';
import { withLeaflet } from 'react-leaflet';
import { FieldWrapper } from 'speed-js-react-web';
import ApiClient from 'speed-js-core/src/api/ApiClient';
import { connectAPI } from 'speed-js-react';
import { HTTP_400_BAD_REQUEST } from 'speed-js-core';
import Spinner from 'esm-web-pro/components/forms/Spinner';
import ActionButtons from 'esm-web-pro/components/forms/ActionButtons';


const messages = defineMessages({
    breakBtn: {
        id: 'break-btn',
        defaultMessage: 'Break',
    },
});

type Props = {
    splice: Object,
    splices: Array<Object>,
    onClose: Function,
    onBreak: Function,
    intl: IntlShape,
    breakSplices: (splices: Array<string>) => Promise<Object>,
}

const BreakSpliceForm = (props: Props) => {
    const {
        splice, splices, intl: { formatMessage }, onClose, onBreak, breakSplices,
    } = props;

    const [spliceFirstFiber, spliceSecondFiber] = [...splice.fiber_starts, ...splice.fiber_ends];

    const [range, setRange] = React.useState(1);
    const [submitting, setSubmitting] = React.useState(false);
    const [splicesForBreaking, setSplicesForBreaking] = React.useState([]);
    const [rangeError, setRangeError] = React.useState<string>(null);

    const isValidSplice = (firstFiber, secondFiber) => {
        let fiber1 = firstFiber;
        let fiber2 = secondFiber;
        let reverse = false;
        let valid = spliceFirstFiber.container.id === fiber1.container.id
            && spliceSecondFiber.container.id === fiber2.container.id;

        if (!valid && spliceSecondFiber.container.id === fiber1.container.id
            && spliceFirstFiber.container.id === fiber2.container.id) {
            valid = true;
            reverse = true;
        }
        if (!valid) return [false, false];

        if (reverse) {
            fiber2 = firstFiber;
            fiber1 = secondFiber;
        }
        if (!(fiber1.sequence_number > spliceFirstFiber.sequence_number)
            || fiber1.sequence_number - fiber2.sequence_number
            !== spliceFirstFiber.sequence_number - spliceSecondFiber.sequence_number) {
            return [false, reverse];
        }

        return [true, reverse];
    };

    // eslint-disable-next-line valid-jsdoc
    /**
     Returns an array with spliced fibers from the same cables as the main splice in array where the main splice uuid
     is number 1 and each next splice is under the number of the splice following consecutive order. If a splice for a
     specific place does not exist it is replaced by null.
     Example: Between cables 1 and 3:
     If Splice between fibers 4 and 5 is number 1, then splice between fibers 6 and 7 is under number 3 and the array
     will look as following [<splice-1-uuid>, null, <splice-2-uuid>]
     * */
    const calculateValidSplicesForBreaking = () => {
        let validSplices = [];
        splices.forEach((sp) => {
            const [firstFiber, secondFiber] = [...sp.fiber_starts, ...sp.fiber_ends];
            const [valid, reverse] = isValidSplice(firstFiber, secondFiber);
            if (valid) {
                const spliceIndex = (reverse ? secondFiber.sequence_number : firstFiber.sequence_number)
                    - spliceFirstFiber.sequence_number - 1;
                if (spliceIndex > validSplices.length) {
                    validSplices = validSplices.concat([...Array(spliceIndex - validSplices.length)].map(() => null));
                    validSplices.push(sp.uuid);
                } else {
                    validSplices[spliceIndex] = (sp.uuid);
                }
            }
        });
        setSplicesForBreaking([splice.uuid, ...validSplices]);
    };

    const memoizedCalculateValidSplicesForBreaking = React.useCallback(
        calculateValidSplicesForBreaking, [splice, splices, rangeError],
    );

    React.useEffect(() => {
        memoizedCalculateValidSplicesForBreaking();
    }, [memoizedCalculateValidSplicesForBreaking]);

    const renderRangeSelect = () => {
        const rangeOfConsecutiveSplices = Object.values(splicesForBreaking).indexOf(null);
        const maxRange = rangeOfConsecutiveSplices < 0
            ? Object.values(splicesForBreaking).length : rangeOfConsecutiveSplices;

        return (
            <FieldWrapper
                key="splice-range-field"
                label={(
                    <FormattedMessage
                        id="break-splice-range-title"
                        defaultMessage="Range"
                        values={{ first: 1, second: maxRange }}
                    />
                )}
            >
                <TextInput
                    name="break-splice-range-input"
                    type="number"
                    onChange={(e) => {
                        const choice = Number(e.target.value);
                        if (!choice || (choice && (choice > maxRange || choice < 0))) {
                            setRangeError(
                                <FormattedMessage
                                    id="break-splice-error-range"
                                    defaultMessage="The chosen value must be between {first} and {maxRange}."
                                    values={{ first: 1, maxRange }}
                                />,
                            );
                        } else {
                            setRangeError(null);
                        }
                        setRange(choice);
                    }}
                    min={1}
                    max={maxRange}
                    disabled={maxRange <= 1}
                    value={range || ''}
                />
                {rangeError && (
                    <Text
                        color="status-error"
                        alignSelf="center"
                        margin={{ top: '10px' }}
                    >
                        {rangeError}
                    </Text>
                )}
            </FieldWrapper>
        );
    };

    const handleSave = () => {
        setSubmitting(true);
        breakSplices([...splicesForBreaking].splice(0, range)).then((response) => {
            setSubmitting(false);
            if (response.status === HTTP_400_BAD_REQUEST) {
                setRangeError(response.data.error);
            } else {
                onBreak();
            }
        });
    };

    return (
        <>
            <Spinner loading={submitting} />
            <Box height="100%">
                <Box
                    direction="row"
                    align="center"
                    height="100%"
                    overflow="auto"
                    style={{ position: 'unset' }}
                >
                    <Box alignSelf="stretch" flex justify="between">
                        <Box flex={{ shrink: 0 }} pad={{ bottom: 'small' }}>
                            <Box height="12px" flex={{ shrink: 0 }} />
                            <Box pad={{ horizontal: 'medium' }}>
                                {renderRangeSelect()}
                            </Box>
                        </Box>
                        <ActionButtons
                            onClose={onClose}
                            disabled={!range || !!rangeError}
                            onSave={handleSave}
                            saveButtonLabel={formatMessage(messages.breakBtn)}
                        />
                    </Box>
                </Box>
            </Box>
        </>
    );
};

const apiCallback = (apiClient: ApiClient) => ({
    breakSplices: {
        send: (splices) => apiClient.resources.splice.break(splices),
    },
});

export default connectAPI(apiCallback)(
    withLeaflet(injectIntl(BreakSpliceForm)),
);
